import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

type SocialProvider = 'google' | 'microsoft'

/** Subset of a better-auth client error we care about for messaging. */
type AuthError = { message?: string; code?: string }

/** How a two-step verification code is checked. */
export type TwoFactorKind = 'totp' | 'otp' | 'backup'

/** Shape returned by the better-auth email/reset client methods. */
type AuthResult = { error?: AuthError | null }

type UseChatbotAuthFormDeps = {
    signInWithEmail: (
        email: string,
        password: string,
        rememberMe?: boolean,
    ) => Promise<AuthResult>
    signUpWithEmail: (params: {
        email: string
        password: string
        name: string
    }) => Promise<AuthResult>
    requestPasswordReset: (
        email: string,
        redirectTo: string,
    ) => Promise<AuthResult>
    /** The installation's app, from the agent access: home of the reset page. */
    frontendUrl: () => string | undefined
    signInWithSocialPopup: (
        provider: SocialProvider,
        onAuthSuccess?: () => void | Promise<void>,
    ) => Promise<{ popupBlocked: boolean }>
    /** True while an email sign-in waits for its second factor. */
    isTwoFactorPending: () => boolean
    verifyTwoFactor: (kind: TwoFactorKind, code: string) => Promise<AuthResult>
    sendTwoFactorCode: () => Promise<AuthResult>
    cancelTwoFactor: () => void
    /** Refetches the current session (after a successful credential sign-in). */
    refetchSession: () => Promise<unknown>
    /** Reloads the agent access state once the user is authenticated. */
    reload: () => void | Promise<void>
}

/**
 * Shared login-screen logic for the web-component widgets (floating and
 * embedded). Centralizes error mapping, loading state and the sign-in /
 * sign-up / reset / social handlers so both widgets behave identically.
 */
export function useChatbotAuthForm(deps: UseChatbotAuthFormDeps) {
    const { t } = useI18n({ useScope: 'global' })

    const authError = ref<string>()
    /**
     * Non-error feedback shown on a dedicated success screen (e.g. after
     * sign-up or a password-reset request). Kept separate from `authError`
     * so these "check your inbox" messages aren't styled as failures.
     */
    const authSuccess = ref<{ title: string; message: string }>()
    const authLoading = ref(false)

    // The client's `translateAuthError` hook has already written the message
    // in the user's language.
    const mapAuthError = (error?: AuthError | null): string =>
        error?.message ?? t('auth.genericError')

    /** Runs an auth action with shared error/loading handling. */
    async function runAuthAction(action: () => Promise<void>): Promise<void> {
        authError.value = undefined
        authSuccess.value = undefined
        authLoading.value = true
        try {
            await action()
        } catch {
            authError.value = t('auth.genericError')
        } finally {
            authLoading.value = false
        }
    }

    async function handleSignIn(payload: {
        email: string
        password: string
        rememberMe: boolean
    }): Promise<void> {
        await runAuthAction(async () => {
            const { error } = await deps.signInWithEmail(
                payload.email,
                payload.password,
                payload.rememberMe,
            )
            if (error) {
                authError.value = mapAuthError(error)
                return
            }
            // The password was right and the account asks for a code: the
            // login screen moves to that step, nothing to reload yet.
            if (deps.isTwoFactorPending()) {
                return
            }
            await deps.refetchSession()
            await deps.reload()
        })
    }

    // The challenge is gone (expired, or too many attempts): only a new
    // sign-in opens another one, so the screen goes back to the form.
    const CHALLENGE_LOST_CODES = new Set([
        'INVALID_TWO_FACTOR_COOKIE',
        'TOO_MANY_ATTEMPTS_REQUEST_NEW_CODE',
    ])

    async function handleVerifyTwoFactor(payload: {
        kind: TwoFactorKind
        code: string
    }): Promise<void> {
        await runAuthAction(async () => {
            const { error } = await deps.verifyTwoFactor(
                payload.kind,
                payload.code,
            )
            if (error) {
                authError.value = mapAuthError(error)
                if (error.code && CHALLENGE_LOST_CODES.has(error.code)) {
                    deps.cancelTwoFactor()
                }
                return
            }
            await deps.refetchSession()
            await deps.reload()
        })
    }

    async function handleSendTwoFactorCode(): Promise<void> {
        await runAuthAction(async () => {
            const { error } = await deps.sendTwoFactorCode()
            if (error) {
                authError.value = mapAuthError(error)
                if (error.code && CHALLENGE_LOST_CODES.has(error.code)) {
                    deps.cancelTwoFactor()
                }
            }
        })
    }

    function handleCancelTwoFactor(): void {
        authError.value = undefined
        deps.cancelTwoFactor()
    }

    async function handleSignUp(payload: {
        email: string
        password: string
        name: string
    }): Promise<void> {
        await runAuthAction(async () => {
            const { error } = await deps.signUpWithEmail(payload)
            if (error) {
                authError.value = mapAuthError(error)
                return
            }
            // After sign-up the user must verify their email before logging in.
            authSuccess.value = {
                title: t('auth.signUpSuccessTitle'),
                message: t('auth.verifyEmail'),
            }
        })
    }

    async function handleReset(payload: { email: string }): Promise<void> {
        await runAuthAction(async () => {
            const frontendUrl = deps.frontendUrl()
            // The link in the mail leads to the app's reset page. A backend
            // that does not say where the app is (one older than the widget)
            // would send a link that leads nowhere, and the widget would
            // still say the mail is on its way.
            if (!frontendUrl) {
                authError.value = t('auth.resetUnavailable')
                return
            }
            const { error } = await deps.requestPasswordReset(
                payload.email,
                `${frontendUrl}/auth/reset-password?email=${encodeURIComponent(payload.email)}`,
            )
            if (error) {
                authError.value = mapAuthError(error)
                return
            }
            authSuccess.value = {
                title: t('auth.resetSentTitle'),
                message: t('auth.resetSent'),
            }
        })
    }

    async function handleSocial(provider: SocialProvider): Promise<void> {
        await runAuthAction(async () => {
            const { popupBlocked } = await deps.signInWithSocialPopup(
                provider,
                () => deps.reload(),
            )
            if (popupBlocked) {
                authError.value = t('auth.popupBlocked')
            }
        })
    }

    return {
        authError,
        authSuccess,
        authLoading,
        handleSignIn,
        handleSignUp,
        handleReset,
        handleSocial,
        handleVerifyTwoFactor,
        handleSendTwoFactorCode,
        handleCancelTwoFactor,
    }
}
